import { authenticatedFetch } from "@lumi/data";
import { useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { Button } from "../../../shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../shared/components/ui/dialog";
import { Input } from "../../../shared/components/ui/input";
import type { SearchResult } from "../../search/hooks/useSearch";
import { useSearch } from "../../search/hooks/useSearch";

interface AddTitleDialogProps {
  readonly isOpen: boolean;
  readonly collectionKey: string;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

/**
 * TitleSearchResultCard - Individual search result item
 */
interface TitleResultCardProps {
  readonly title: SearchResult;
  readonly adding: boolean;
  readonly onAdd: (titleId: string) => Promise<void>;
}

function TitleSearchResultCard({ title, adding, onAdd }: TitleResultCardProps) {
  const handleAdd = () => {
    void onAdd(title.id);
  };

  return (
    <div className="flex items-center gap-6 py-4 hover:bg-neutral-750 transition-colors">
      {title.posterUrlHd && <img src={title.posterUrlHd} alt={title.name} className="w-16 h-24 object-cover rounded" />}
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-medium truncate">{title.name}</h3>
        {title.year && <p className="text-sm text-neutral-400">{title.year}</p>}
        {title.synopsis && <p className="text-sm text-neutral-500 line-clamp-2 mt-1">{title.synopsis}</p>}
      </div>
      <Button onClick={handleAdd} disabled={adding} variant="outline">
        {adding ? "Adding..." : "Add"}
      </Button>
    </div>
  );
}

/**
 * SearchBar Component
 */
interface SearchBarProps {
  readonly query: string;
  readonly searching: boolean;
  readonly onQueryChange: (q: string) => void;
  readonly onSearch: () => void;
}

function SearchBar({ query, searching, onQueryChange, onSearch }: SearchBarProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  return (
    <div className="flex gap-3">
      <Input
        type="text"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search by title, director, cast, genre, country..."
        className="flex-1"
      />
      <Button onClick={onSearch} disabled={searching || !query.trim()} variant="outline">
        {searching ? "Searching..." : "Search"}
      </Button>
    </div>
  );
}

/**
 * SearchResults Component
 */
interface SearchResultsProps {
  readonly results: SearchResult[];
  readonly searching: boolean;
  readonly adding: string | null;
  readonly onAdd: (titleId: string) => Promise<void>;
}

function SearchResults({ results, searching, adding, onAdd }: SearchResultsProps) {
  if (searching) {
    return (
      <div className="text-center py-12 text-neutral-400">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-lumi-accent" />
        <p className="mt-4">Searching titles...</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-sm text-center py-12 text-neutral-600">No titles found. Try a different search query.</div>
    );
  }

  return (
    <div className="space-y-3">
      {results.map((title) => (
        <TitleSearchResultCard key={title.id} title={title} adding={adding === title.id} onAdd={onAdd} />
      ))}
    </div>
  );
}

/**
 * AddTitleDialog - Search for and add titles to a collection
 */
export function AddTitleDialog({ isOpen, collectionKey, onClose, onSuccess }: AddTitleDialogProps) {
  const { accessToken, refreshAuth } = useAuth();
  const { query, results, loading, search, clear } = useSearch({
    debounceMs: 300,
    limit: 20,
  });
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  const handleSearch = () => {
    search(query);
  };

  const handleAddTitle = async (titleId: string) => {
    if (!accessToken) return;

    setAdding(titleId);
    setError(null);

    try {
      await authenticatedFetch(
        `${API_BASE}/v1/admin/collections/${collectionKey}/items`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ titleId }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      onSuccess();
      handleClose();
    } catch (err) {
      console.error("❌ Failed to add title:", err);
      setError("Failed to add title to collection");
      setAdding(null);
    }
  };

  const handleClose = () => {
    clear();
    setError(null);
    setAdding(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] bg-black/50 border border-neutral-800 backdrop-blur-lg drop-shadow-md flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Title to Collection</DialogTitle>
          <DialogDescription className="text-sm text-neutral-400">
            Search for titles to add to this collection
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-1 flex-1 overflow-hidden flex flex-col">
          <SearchBar query={query} searching={loading} onQueryChange={search} onSearch={handleSearch} />

          {error && (
            <div className="p-3 bg-red-900/20 border border-red-800 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto pr-2">
            <SearchResults results={results} searching={loading} adding={adding} onAdd={handleAddTitle} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
